import { useQuery } from "@tanstack/react-query";
import { stats } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Users, Truck, UtensilsCrossed, LogIn, QrCode, Clock, AlertTriangle, Activity } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line } from "recharts";

export default function Dashboard() {
  const { data: dashboardStats, isLoading: statsLoading } = useQuery({
    queryKey: ['dashboard-stats'],
    queryFn: stats.dashboard,
    refetchInterval: 30000, // Refresh every 30 seconds
  });

  const { data: pastiData } = useQuery({
    queryKey: ['pasti-giornalieri'],
    queryFn: () => stats.pastiGiornalieri(7),
  });

  const { data: accessiData } = useQuery({
    queryKey: ['accessi-orari'],
    queryFn: stats.accessiOrari,
  });

  const statCards = [
    {
      title: "Presenti nel Campo",
      value: dashboardStats?.presenti_campo || 0,
      icon: Users,
      color: "text-[#0B1E3D]",
      bg: "bg-blue-50",
    },
    {
      title: "Persone Registrate",
      value: dashboardStats?.persone_registrate || 0,
      icon: Users,
      color: "text-slate-600",
      bg: "bg-slate-50",
    },
    {
      title: "Mezzi Operativi",
      value: dashboardStats?.mezzi_operativi || 0,
      icon: Truck,
      color: "text-green-600",
      bg: "bg-green-50",
    },
    {
      title: "Cucine Operative",
      value: dashboardStats?.cucine_operative || 0,
      icon: UtensilsCrossed,
      color: "text-orange-600",
      bg: "bg-orange-50",
    },
    {
      title: "Accessi Oggi",
      value: dashboardStats?.accessi_oggi || 0,
      icon: LogIn,
      color: "text-emerald-600",
      bg: "bg-emerald-50",
    },
    {
      title: "Pasti Serviti Oggi",
      value: dashboardStats?.pasti_oggi || 0,
      icon: QrCode,
      color: "text-[#DC2626]",
      bg: "bg-red-50",
    },
    {
      title: "Eventi Badge Oggi",
      value: dashboardStats?.eventi_oggi || 0,
      icon: Activity,
      color: "text-purple-600",
      bg: "bg-purple-50",
    },
    {
      title: "Turni Scoperti",
      value: dashboardStats?.turni_scoperti || 0,
      icon: AlertTriangle,
      color: dashboardStats?.turni_scoperti > 0 ? "text-red-600" : "text-green-600",
      bg: dashboardStats?.turni_scoperti > 0 ? "bg-red-50" : "bg-green-50",
    },
  ];

  if (statsLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0B1E3D]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="dashboard-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-slate-900">Dashboard</h1>
          <p className="text-slate-500 mt-1">Stato operativo del campo - {dashboardStats?.data}</p>
        </div>
        <div className="flex items-center gap-2 px-4 py-2 bg-green-50 border border-green-200 rounded-lg">
          <span className="status-dot success"></span>
          <span className="text-sm font-medium text-green-700">Campo Operativo</span>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="stats-grid">
        {statCards.map((stat, index) => (
          <Card key={index} className="stat-card" data-testid={`stat-${stat.title.toLowerCase().replace(/\s+/g, '-')}`}>
            <CardContent className="p-4">
              <div className="flex items-start justify-between">
                <div>
                  <p className="stat-label">{stat.title}</p>
                  <p className={`stat-value mt-2 ${stat.color}`}>{stat.value}</p>
                </div>
                <div className={`p-2 rounded-lg ${stat.bg}`}>
                  <stat.icon className={`h-5 w-5 ${stat.color}`} />
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Pasti Giornalieri */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <UtensilsCrossed className="h-5 w-5 text-orange-600" />
              Pasti Ultimi 7 Giorni
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={pastiData || []}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                  <XAxis 
                    dataKey="data" 
                    tick={{ fontSize: 12 }}
                    tickFormatter={(value) => value.slice(5)}
                  />
                  <YAxis tick={{ fontSize: 12 }} />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: 'white', 
                      border: '1px solid #E2E8F0',
                      borderRadius: '8px'
                    }}
                  />
                  <Bar dataKey="colazione" name="Colazione" fill="#F59E0B" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="pranzo" name="Pranzo" fill="#0B1E3D" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="cena" name="Cena" fill="#DC2626" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Accessi Orari */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <LogIn className="h-5 w-5 text-emerald-600" />
              Accessi Oggi per Ora
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={accessiData || []}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                  <XAxis 
                    dataKey="ora" 
                    tick={{ fontSize: 12 }}
                    interval={3}
                  />
                  <YAxis tick={{ fontSize: 12 }} />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: 'white', 
                      border: '1px solid #E2E8F0',
                      borderRadius: '8px'
                    }}
                  />
                  <Line 
                    type="monotone" 
                    dataKey="entrate" 
                    name="Entrate"
                    stroke="#10B981" 
                    strokeWidth={2}
                    dot={{ fill: '#10B981', strokeWidth: 2 }}
                  />
                  <Line 
                    type="monotone" 
                    dataKey="uscite" 
                    name="Uscite"
                    stroke="#DC2626" 
                    strokeWidth={2}
                    dot={{ fill: '#DC2626', strokeWidth: 2 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Quick Status */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="border-l-4 border-l-green-500">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-50 rounded-lg">
                <Activity className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <p className="text-sm font-medium text-slate-900">Servizi Attivi</p>
                <p className="text-xs text-slate-500">Tutti i servizi operativi</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-blue-500">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-50 rounded-lg">
                <Clock className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="text-sm font-medium text-slate-900">Prossimo Turno</p>
                <p className="text-xs text-slate-500">Cambio turno ore 14:00</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-orange-500">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-orange-50 rounded-lg">
                <UtensilsCrossed className="h-5 w-5 text-orange-600" />
              </div>
              <div>
                <p className="text-sm font-medium text-slate-900">Prossimo Pasto</p>
                <p className="text-xs text-slate-500">Pranzo ore 12:30</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
